import { HttpClient } from '@shared/adapters/HttpClient';
import {
  BoxEvaluateResponse,
  BoxExceptionModel,
  BoxGroupModel,
  BoxRulesetModel,
  CreateBoxExceptionConditionPayload,
  CreateBoxExceptionPayload,
  CreateMarketingRuleActionPayload,
  CreateMarketingRuleConditionPayload,
  CreateMarketingRulePayload,
  MarketingEvaluateResponse,
  MarketingRuleModel,
  MarketingRulesetModel,
  PackagingOptionItem,
  PackagingEvaluationContext,
} from '../models/PackagingRulesets';

interface PaginatedResponse<T> {
  data: T[];
  meta?: {
    current_page?: number;
    currentPage?: number;
    last_page?: number;
    lastPage?: number;
    perPage?: number;
    total?: number;
  };
}

interface PackagingItemApiModel {
  id: number;
  name: string;
  code?: string;
  category?: string;
  active?: boolean;
  width_cm?: number;
  height_cm?: number;
  length_cm?: number;
  max_weight_kg?: number;
  image_url?: string;
}

/**
 * Contrato API (3PL / tenant_external): ver documentación interna «Packaging reglas team external».
 * Resumen: `team_id` en query solo en listados GET y en POST de creación de rulesets;
 * no enviar `team_id` en PUT de ruleset, evaluate, ni en bodies de reglas/excepciones anidadas.
 */
export class PackagingRulesetsApiAdapter {
  constructor(private httpClient: HttpClient) {}

  private withTeamParam(params: Record<string, string | number>, teamId?: number): Record<string, string | number> {
    if (teamId == null) return params;
    return { ...params, team_id: teamId };
  }

  /** Solo POST crear box/marketing ruleset (3PL debe enviar team_id del tenant_external). */
  private withTeamBody<T extends Record<string, unknown>>(payload: T, teamId?: number): T & { team_id?: number } {
    if (teamId == null) return payload;
    return { ...payload, team_id: teamId };
  }

  async createBoxRuleset(
    payload: {
      active: boolean;
      default_box_group_id?: number | null;
      fallback_box_group_id?: number | null;
    },
    teamId?: number,
  ): Promise<BoxRulesetModel> {
    return this.httpClient.post<BoxRulesetModel>('/packaging/box-rulesets', this.withTeamBody(payload, teamId));
  }

  /** PUT: sin team_id en body (dueño ya persistido en el ruleset). */
  async updateBoxRuleset(
    rulesetId: number,
    payload: {
      active: boolean;
      default_box_group_id: number | null;
      fallback_box_group_id?: number | null;
    },
  ): Promise<BoxRulesetModel> {
    return this.httpClient.put<BoxRulesetModel>(`/packaging/box-rulesets/${rulesetId}`, payload);
  }

  async getBoxRulesets(active?: boolean, teamId?: number): Promise<BoxRulesetModel[]> {
    const params: Record<string, string | number> = this.withTeamParam(
      {
        per_page: 20,
        page: 1,
      },
      teamId,
    );

    if (typeof active === 'boolean') {
      params.active = active ? 1 : 0;
    }

    const response = await this.httpClient.get<PaginatedResponse<BoxRulesetModel>>('/packaging/box-rulesets', params);

    return response?.data ?? [];
  }

  async getMarketingRulesets(teamId?: number): Promise<MarketingRulesetModel[]> {
    const params = this.withTeamParam(
      {
        per_page: 100,
        page: 1,
      },
      teamId,
    );

    const response = await this.httpClient.get<PaginatedResponse<MarketingRulesetModel>>(
      '/packaging/marketing-rulesets',
      params,
    );

    return response?.data ?? [];
  }

  async createMarketingRuleset(
    payload: { active: boolean } = { active: true },
    teamId?: number,
  ): Promise<MarketingRulesetModel> {
    return this.httpClient.post<MarketingRulesetModel>(
      '/packaging/marketing-rulesets',
      this.withTeamBody(payload, teamId),
    );
  }

  /** GET detalle: solo `{id}` en ruta; autorización por team del registro. */
  async getBoxRulesetById(rulesetId: number): Promise<BoxRulesetModel> {
    const raw = await this.httpClient.get<BoxRulesetModel>(`/packaging/box-rulesets/${rulesetId}`);

    const defaultId = raw.default_packaging_item_id ?? raw.default_packaging_item?.id ?? 0;
    const fallbackId = raw.fallback_packaging_item_id ?? raw.fallback_packaging_item?.id ?? null;

    return {
      ...raw,
      default_box_group_id: raw.default_box_group_id ?? null,
      fallback_box_group_id: raw.fallback_box_group_id ?? null,
      default_packaging_item_id: defaultId,
      fallback_packaging_item_id: fallbackId,
    };
  }

  async getMarketingPackagingItems(active = true, teamId?: number): Promise<PackagingOptionItem[]> {
    const params = new URLSearchParams();
    params.append('categories[]', 'marketing_item');
    params.append('active', active ? '1' : '0');
    params.append('per_page', '100');
    params.append('page', '1');
    if (teamId != null) params.append('team_id', String(teamId));

    const response = await this.httpClient.get<PaginatedResponse<PackagingItemApiModel>>('/packaging', params as any);

    const colorPalette: Array<{ colorClass: string; iconClass: string }> = [
      { colorClass: 'bg-bg-info', iconClass: 'text-info' },
      { colorClass: 'bg-badge-bg-status-3', iconClass: 'text-badge-status-3' },
      { colorClass: 'bg-bg-success', iconClass: 'text-success' },
      { colorClass: 'bg-bg-warning', iconClass: 'text-warning' },
      { colorClass: 'bg-badge-bg-status-4', iconClass: 'text-badge-status-4' },
    ];

    return (response?.data ?? []).map((item, index) => {
      const palette = colorPalette[index % colorPalette.length];
      return {
        id: item.id,
        name: item.name,
        description: item.code ? `Código: ${item.code}` : item.name,
        imageUrl: item.image_url,
        colorClass: palette.colorClass,
        iconClass: palette.iconClass,
      };
    });
  }

  /**
   * Grupos de cajas activos.
   * Para cliente hijo (3PL), `/box-groups` filtra con advanced_filters por team_id (no basta query suelta).
   */
  async getAllBoxGroups(teamId?: number, boxCategory?: 'own_box' | 'customer_box'): Promise<BoxGroupModel[]> {
    if (teamId == null) {
      const response = await this.httpClient.get<PaginatedResponse<BoxGroupModel>>('/box-groups', {
        active: 1,
        per_page: 100,
        page: 1,
        ...(boxCategory ? { box_category: boxCategory } : {}),
      });
      return response?.data ?? [];
    }

    const params: Record<string, string | number | string[]> = {
      active: 1,
      per_page: 100,
      page: 1,
      'sorting[column]': 'id',
      'sorting[direction]': 'desc',
      'advanced_filters[logic]': 'AND',
      'advanced_filters[filters][0][column]': 'team_id',
      'advanced_filters[filters][0][operator]': 'in',
      'advanced_filters[filters][0][value][]': [String(teamId)],
      ...(boxCategory ? { box_category: boxCategory } : {}),
    };

    const response = await this.httpClient.get<PaginatedResponse<BoxGroupModel>>('/box-groups', params);
    return response?.data ?? [];
  }

  async getBoxRulesetExceptions(rulesetId: number): Promise<BoxExceptionModel[]> {
    const response = await this.httpClient.get<PaginatedResponse<BoxExceptionModel>>(
      `/packaging/box-rulesets/${rulesetId}/exceptions`,
      {
        per_page: 50,
        page: 1,
      },
    );

    return response?.data ?? [];
  }

  async createBoxException(rulesetId: number, payload: CreateBoxExceptionPayload): Promise<BoxExceptionModel> {
    return this.httpClient.post<BoxExceptionModel>(
      `/packaging/box-rulesets/${rulesetId}/exceptions`,
      payload as unknown as Record<string, unknown>,
    );
  }

  async updateBoxException(
    exceptionId: number,
    payload: { name: string; packaging_item_id: number; priority: number; active: boolean },
  ): Promise<BoxExceptionModel> {
    return this.httpClient.put<BoxExceptionModel>(`/packaging/box-exceptions/${exceptionId}`, payload);
  }

  async deleteBoxException(exceptionId: number): Promise<void> {
    await this.httpClient.delete<void>(`/packaging/box-exceptions/${exceptionId}`);
  }

  async createBoxExceptionCondition(exceptionId: number, payload: CreateBoxExceptionConditionPayload): Promise<void> {
    await this.httpClient.post<void>(
      `/packaging/box-exceptions/${exceptionId}/conditions`,
      payload as unknown as Record<string, unknown>,
    );
  }

  async createMarketingRule(rulesetId: number, payload: CreateMarketingRulePayload): Promise<MarketingRuleModel> {
    return this.httpClient.post<MarketingRuleModel>(
      `/packaging/marketing-rulesets/${rulesetId}/rules`,
      payload as unknown as Record<string, unknown>,
    );
  }

  /** El backend expone PUT/DELETE en recurso plano, no bajo …/marketing-rulesets/{id}/rules/{ruleId}. */
  async updateMarketingRule(
    ruleId: number,
    payload: { name: string; priority: number; active: boolean },
  ): Promise<MarketingRuleModel> {
    return this.httpClient.put<MarketingRuleModel>(`/packaging/marketing-rules/${ruleId}`, payload);
  }

  async deleteMarketingRule(ruleId: number): Promise<void> {
    await this.httpClient.delete<void>(`/packaging/marketing-rules/${ruleId}`);
  }

  async createMarketingRuleCondition(ruleId: number, payload: CreateMarketingRuleConditionPayload): Promise<void> {
    await this.httpClient.post<void>(
      `/packaging/marketing-rules/${ruleId}/conditions`,
      payload as unknown as Record<string, unknown>,
    );
  }

  async createMarketingRuleAction(ruleId: number, payload: CreateMarketingRuleActionPayload): Promise<void> {
    await this.httpClient.post<void>(
      `/packaging/marketing-rules/${ruleId}/actions`,
      payload as unknown as Record<string, unknown>,
    );
  }

  /** POST evaluate: solo body `context` (obligatorio). */
  async evaluateBoxRuleset(rulesetId: number, context: PackagingEvaluationContext): Promise<BoxEvaluateResponse> {
    return this.httpClient.post<BoxEvaluateResponse>(`/packaging/box-rulesets/${rulesetId}/evaluate`, {
      context: {
        total_amount: Number(context.total_amount || 0),
        country: context.country,
        fragile: context.fragile,
        campaign: context.campaign ?? null,
      },
    });
  }

  async evaluateMarketingRuleset(
    rulesetId: number,
    context: PackagingEvaluationContext,
  ): Promise<MarketingEvaluateResponse> {
    return this.httpClient.post<MarketingEvaluateResponse>(`/packaging/marketing-rulesets/${rulesetId}/evaluate`, {
      context: {
        total_amount: Number(context.total_amount || 0),
        country: context.country,
        fragile: context.fragile,
        campaign: context.campaign ?? null,
      },
    });
  }

  async getBoxGroupById(id: number): Promise<BoxGroupModel> {
    return this.httpClient.get<BoxGroupModel>(`/box-groups/${id}`);
  }

  async getCountries(): Promise<{ id: number; name: string; iso2: string }[]> {
    const raw = await this.httpClient.get<any>('/countries');
    const list = Array.isArray(raw) ? raw : (raw.data ?? []);
    return list.map((c: any) => ({ id: c.id, name: c.name, iso2: c.iso2 }));
  }
}
